import { displayAbn, displayAcn } from "../../../../Utils/companyNumber";
import { formatMobile } from "../../../common/MobileField";
import { useEffect, useRef, useState } from "react";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import DataTable from "react-data-table-component";
import { Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import {
    getOperatorCompaniesService,
    getOperatorCompanyByIdService,
    updateOperatorCompanyStatusService,
} from "../../../../Services/CRMAdminServices/CRMOperator";
import EditMemberDetails from "./EditMemberDetails";



const customStyles = {
    head: {
        style: {
            fontWeight: "800",
            color: "#0000ff",
            fontSize: "14px",
        },
    },
};

type StatusFilter = "" | "true" | "false";

const dash = (val: any) => (val === null || val === undefined || val === "" ? "-" : val);

// N21: API datetimes are UTC without a marker - formatDateTime reads them as UTC
// and renders Australian Eastern time.
const formatDate = (val: string | null) => formatDateTime(val);

const OperatorMembersList = () => {
    const [searchInput, setSearchInput] = useState("");
    const [searchText, setSearchText] = useState("");
    const [isActiveFilter, setIsActiveFilter] = useState<StatusFilter>("true");

    const [statusModal, setStatusModal] = useState(false);
    const [pendingCompany, setPendingCompany] = useState<any>(null); 

    // Edit straight from the list: the row only carries a summary, so the full
    // member is read first and the same window as the member page is opened.
    const [editCompany, setEditCompany] = useState<any>(null);
    const [editLoadingId, setEditLoadingId] = useState<number | null>(null);

    const openEdit = async (row: any) => {
        setEditLoadingId(row.companyId);
        try {
            const res: any = await getOperatorCompanyByIdService(row.companyId);
            setEditCompany(res?.data || null);
        } catch {
            toast.error("Could not open this member. Try again.");
        } finally {
            setEditLoadingId(null);
        }
    };

    const [tableScrollHeight, setTableScrollHeight] = useState("400px");
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const navigate = useSafeNavigate();

    useEffect(() => {
        const t = setTimeout(() => setSearchText(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const queryParams = {
        ...(searchText ? { searchText } : {}),
        ...(isActiveFilter !== "" ? { isActive: isActiveFilter === "true" } : {}),
    };

    const {
        data: companiesData,
        isLoading,
        isError,
        refetch,
    } = useGetRequestQuery<any>(
        ["getOperatorCompanies", searchText, isActiveFilter],
        () => getOperatorCompaniesService(queryParams),
        { enabled: true }
    );

    const companies = !isError ? companiesData?.data || [] : [];

    // Label + count reflect whichever filter is currently selected, so the
    // number on screen always matches what's actually in the table below.
    const filterLabel =
        isActiveFilter === "true" ? "Active" : isActiveFilter === "false" ? "Disabled" : "All";
    const totalCount = companies.length;

    useEffect(() => {
        const updateTableHeight = () => {
            if (!tableWrapperRef.current) return;
            const wrapperHeight = tableWrapperRef.current.clientHeight;
            setTableScrollHeight(`${Math.max(wrapperHeight - 8, 200)}px`);
        };
        updateTableHeight();
        window.addEventListener("resize", updateTableHeight);
        return () => window.removeEventListener("resize", updateTableHeight);
    }, [companies.length, isLoading]);

    const { mutate: toggleStatus, isPending: isToggling } = useMutationRequestQuery(
        "updateOperatorCompanyStatus",
        updateOperatorCompanyStatusService,
        {
            onSuccess: (res: any) => {
                toast.success(res?.data?.message || "Member status updated.");
                setStatusModal(false);
                setPendingCompany(null);
                refetch();
            },
            onError: (err: any) => {
                if (err?.response?.status === 404) {
                    toast.error("This company no longer exists. Refreshing the list.");
                    refetch();
                } else {
                    toast.error(err?.response?.data?.message || "Failed to update member status.");
                }
                setStatusModal(false);
                setPendingCompany(null);
            },
        }
    );

    const openStatusModal = (row: any) => {
        setPendingCompany(row);
        setStatusModal(true);
    };

    const confirmToggle = () => {
        if (!pendingCompany) return;
        toggleStatus({ companyId: pendingCompany.companyId, isActive: !pendingCompany.isActive });
    };

    const columns = [
        {
            name: "Business Name",
            selector: (row: any) => row.businessName,
            sortable: true,
            cell: (row: any) => (
                <Link to={`/OperatorMemberDetail/${row.companyId}`}>{row.businessName}</Link>
            ),
            width: "14rem",
        },
        { name: "ACN", selector: (row: any) => dash(displayAcn(row.acn)), sortable: true, width: "9rem" },
        { name: "ABN", selector: (row: any) => dash(displayAbn(row.abn)), sortable: true, width: "10rem" },
        { name: "Contact", selector: (row: any) => dash(row.contactName), sortable: true, width: "10rem" },
        { name: "Mobile", selector: (row: any) => dash(formatMobile(row.contactMobile)), sortable: false, width: "9rem" },
        { name: "Email", selector: (row: any) => dash(row.contactEmail), sortable: false, width: "14rem" },
        { name: "Staff", selector: (row: any) => row.staffCount, sortable: true, width: "6rem" },
        { name: "Searches", selector: (row: any) => row.searchCount, sortable: true, width: "7rem" },
        { name: "Reports", selector: (row: any) => row.reportCount, sortable: true, width: "7rem" },
        { name: "EOIs", selector: (row: any) => row.eoiCount, sortable: true, width: "6rem" },
        {
            name: "Last Activity",
            selector: (row: any) => row.lastActivityOn,
            sortable: true,
            cell: (row: any) => formatDate(row.lastActivityOn),
            width: "11rem",
        },
        {
            name: "Status",
            width: "16rem",
            cell: (row: any) => (
                <div className="d-flex align-items-center gap-2">
                    <Badge color={row.isActive ? "success" : "secondary"}>
                        {row.isActive ? "Active" : "Disabled"}
                    </Badge>
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={() => openEdit(row)}
                        disabled={editLoadingId === row.companyId}
                    >
                        {editLoadingId === row.companyId ? "Opening..." : "Edit"}
                    </Button>
                    <Button size="sm" color={row.isActive ? "danger" : "success"} outline onClick={() => openStatusModal(row)}>
                        {row.isActive ? "Disable" : "Enable"}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div
                className="px-4 pt-4"
                style={{
                    height: "90vh",
                    overflow: "hidden",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div
                    style={{
                        borderRadius: "8px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        padding: "10px",
                        backgroundColor: "#fff",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <h3 className="f-w-900 heading-color mb-0">Members</h3>
                            {!isLoading && (
                                <Badge color="primary" pill style={{ fontSize: "13px" }}>
                                    {filterLabel}: {totalCount}
                                </Badge>
                            )}
                        </div>
                        <Button color="primary" onClick={() => navigate("/AddOperatorMember")}>
                            Create member
                        </Button>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                        <div className="me-3">
                            <label className="me-2">Status:</label>
                            <select
                                value={isActiveFilter}
                                onChange={(e) => setIsActiveFilter(e.target.value as StatusFilter)}
                                className="form-control d-inline-block"
                                style={{ width: 160 }}
                            >
                                <option value="true">Active only</option>
                                <option value="false">Disabled only</option>
                                <option value="">All</option>
                            </select>
                        </div>

                        <div className="me-3">
                            <label className="me-2">Search:</label>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="form-control d-inline-block"
                                style={{ width: 260 }}
                                placeholder="Business name, contact name or email"
                            />
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                            <Spinner color="primary" />
                        </div>
                    ) : companies.length !== 0 ? (
                        <div ref={tableWrapperRef} style={{ flex: 1, minHeight: 0 }}>
                            <DataTable
                                columns={columns}
                                data={companies}
                                customStyles={customStyles}
                                responsive
                                fixedHeader
                                fixedHeaderScrollHeight={tableScrollHeight}
                                persistTableHead
                                defaultSortFieldId={1}
                            />
                        </div>
                    ) : (
                        <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                            <h6>No members found.</h6>
                        </div>
                    )}
                </div>
            </div>

            <EditMemberDetails
                company={editCompany}
                isOpen={!!editCompany}
                toggle={() => setEditCompany(null)}
                onSaved={() => refetch()}
            />

            {/* Enable / Disable confirmation */}
            <Modal isOpen={statusModal} toggle={() => setStatusModal(!statusModal)} centered>
                <ModalHeader toggle={() => setStatusModal(!statusModal)}>
                    {pendingCompany?.isActive ? "Disable member account" : "Enable member account"}
                </ModalHeader>
                <ModalBody>
                    {pendingCompany?.isActive ? (
                        <>
                            Disable <strong>{pendingCompany?.businessName}</strong>? Every login in this
                            company will stop working immediately. Nothing is deleted - their reports and
                            EOIs stay, and re-enabling restores access exactly as it was.
                        </>
                    ) : (
                        <>
                            Re-enable <strong>{pendingCompany?.businessName}</strong>? Access will be restored
                            exactly as it was before.
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setStatusModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color={pendingCompany?.isActive ? "danger" : "success"}
                        onClick={confirmToggle}
                        disabled={isToggling}
                    >
                        {isToggling ? "Saving..." : pendingCompany?.isActive ? "Disable" : "Enable"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default OperatorMembersList;